import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
  Alert,
  Container,
  Divider,
  Tooltip,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"; 
import { COLORS, RADIUS_OPTIONS } from "./constants";

const SearchControls = ({
  searchQuery,
  setSearchQuery,
  radius,
  setRadius,
  loading,
  locating,
  error,
  searchedLocation,
  onSearch,
  onUseMyLocation,
  onClearLocation,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [localSearch, setLocalSearch] = useState(searchQuery);

  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
        onSearch(localSearch); 
    }
  };

  const handleSearchClick = () => {
    onSearch(localSearch); 
  };

  const handleClearInput = () => {
      setLocalSearch("");
  };

  const isUsingGPS = searchedLocation?.name === "Your Location";

  const getShortRadiusLabel = (val) => {
    const option = RADIUS_OPTIONS.find((opt) => opt.value === val);
    return option ? option.label.replace(" Kilometers", "km") : `${val}km`;
  };

  return (
    // ⬇️ FIX: Increased mt back to 9 to clear the blue header graphics
    <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 9 }, mb: 2, px: {xs: 2, md: 3} }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Paper
          elevation={isMobile ? 2 : 4}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "800px",
            p: isMobile ? "4px 4px 4px 12px" : "4px",
            borderRadius: "50px",
            border: `1px solid ${COLORS.border}`,
            backgroundColor: "#fff",
            transition: "box-shadow 0.3s ease",
            "&:hover": {
              boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', pl: {xs: 0, md: 2} }}>
            <SearchIcon sx={{ color: COLORS.textSecondary, mr: 1, fontSize: isMobile ? 20 : 24 }} />
            <TextField
              fullWidth
              variant="standard"
              placeholder={isMobile ? "Search location..." : "Search location (e.g., Quezon City)"}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                disableUnderline: true,
                style: { fontSize: isMobile ? "0.95rem" : "1.1rem", fontWeight: 500 }
              }}
            />
            {localSearch && (
              <IconButton size="small" onClick={handleClearInput}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ height: 20, my: "auto", mx: {xs: 0.5, md: 1} }} />

          {!isMobile ? (
            <FormControl variant="standard" sx={{ minWidth: 100, mx: 1 }}>
              <Select
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                disableUnderline
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: COLORS.text,
                  "& .MuiSelect-select": { py: 1 }
                }}
              >
                {RADIUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
             <FormControl variant="standard" sx={{ mx: 0.5 }}>
                <Select 
                  value={radius} 
                  onChange={(e) => setRadius(e.target.value)}
                  disableUnderline
                  displayEmpty
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                       <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.primary }}>
                          {getShortRadiusLabel(selected)}
                       </Typography>
                       <ArrowDropDownIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
                    </Box>
                  )}
                  sx={{ 
                    bgcolor: COLORS.primaryLight,
                    borderRadius: "20px",
                    py: 0.5,
                    px: 1.5,
                    "& .MuiSelect-icon": { display: 'none' },
                    "& .MuiSelect-select": { pr: '0 !important', py: 0, display: 'flex' }
                  }}
                >
                   {RADIUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.9rem' }}>
                      {getShortRadiusLabel(opt.value)}
                    </MenuItem>
                  ))}
                </Select>
             </FormControl>
          )}

          <Box sx={{ display: 'flex', gap: 0.5, pr: 0.5, pl: {xs: 0.5, md: 0} }}>
            <Tooltip title="Use my location">
              <IconButton 
                onClick={onUseMyLocation} 
                disabled={locating}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  color: locating ? COLORS.textSecondary : COLORS.primary,
                  bgcolor: locating ? "transparent" : COLORS.primaryLight,
                  "&:hover": { bgcolor: "#b2ebf2" },
                  p: isMobile ? 1 : 1.5
                }}
              >
                {locating ? <CircularProgress size={20} /> : <MyLocationIcon fontSize={isMobile ? "small" : "medium"} />}
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              onClick={handleSearchClick}
              disabled={loading}
              sx={{
                borderRadius: "40px",
                px: { xs: 2.5, md: 4 },
                py: { xs: 1, md: 1 },
                minWidth: {xs: 'auto', md: 'auto'},
                backgroundColor: COLORS.primary,
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                fontSize: {xs: "0.9rem", md: "1rem"},
                boxShadow: "0 4px 12px rgba(0, 187, 212, 0.3)",
                "&:hover": { 
                  backgroundColor: COLORS.primaryDark,
                  boxShadow: "0 6px 15px rgba(0, 187, 212, 0.4)",
                },
              }}
            >
              {loading ? (isMobile ? "..." : "Searching...") : "Search"}
            </Button>
          </Box>
        </Paper>

        {isUsingGPS && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Using your current GPS location"
            onDelete={onClearLocation}
            sx={{
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
              fontWeight: 600,
              animation: "fadeIn 0.5s ease"
            }}
          />
        )}

        {error && (
          <Alert 
            severity="warning" 
            sx={{ 
              borderRadius: "12px", 
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              animation: "shake 0.4s ease",
              width: '100%',
              maxWidth: '800px'
            }}
          >
            {error}
          </Alert>
        )}
      </Box>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
      `}</style>
    </Container>
  );
};

export default SearchControls;